import { Stack, Container, Grid } from '@mui/material';
import { type NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { NavLinks } from 'components/Header';
import { TritonFooter } from 'components/TritonFooter';
import { TritonLogo } from 'components/TritonLogo';
import { theme } from 'styles/mui-theme';

const ReportReissuanceHistory: NextPage = () => {
    const intl = useIntl();

    const css: Record<string, React.CSSProperties> = {
        date: { fontWeight: 'bolder' },
        history: { fontSize: 20 },
        div: { marginBlockStart: '1em', marginBlockEnd: '1em' },
    };

    const title = intl.formatMessage({
        defaultMessage: 'Report Reissuance History',
        description: 'report reissuance history',
    });

    return (
        <>
            <Head>
                <title>{title}</title>
            </Head>
            <Container>
                <Stack>
                    <Grid
                        container
                        spacing={1}
                        sx={{
                            minHeight: 'calc(100vh - 30px)', // show some extra content to hint at scrolling
                            pt: 2,
                            [theme.breakpoints.down('md')]: {
                                minHeight: 'calc(100vh)',
                            },
                        }}
                        direction="column"
                    >
                        <Grid item xs={12}>
                            <Grid container alignItems="center">
                                <Grid item xs>
                                    <Link href="/">
                                        <TritonLogo
                                            css={{
                                                [theme.breakpoints.only('xs')]: {
                                                    width: '100%',
                                                    maxWidth: 100,
                                                    verticalAlign: 'middle',
                                                },
                                            }}
                                        />
                                    </Link>
                                </Grid>
                                <Grid item>
                                    <NavLinks />
                                </Grid>
                            </Grid>
                        </Grid>
                        <h1>{title}</h1>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'May 21st, 2024: Reissuance of the April 2024 Australian Podcast Rankers',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Addition of 2 podcasts for Australian Top Podcast Ranker.',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'Correction of rank of 2 podcasts for Australian-made Top Podcast Ranker.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'January 26, 2023: Reissuance of the December 2022 New Zealand & Australian Podcast Rankers',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Republished the files with updated SEN numbers.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage: 'June 7, 2022: Reissuance of the April 2022 US Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>
                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Correction of program.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'February 22, 2022: Reissuance of the December 2021 LATAM Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Correction of publisher.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'February 1, 2022: Reissuance of the December 2021 Australian Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Correction of network and rank.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'December 10, 2021: Reissuance of the October 2021 Australian Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Correction of network.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'September 22, 2021: Reissuance of the August 2021 Netherlands Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Correction of arrows under rank.',
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'May 18, 2021: Reissuance of the April 2020 Australian Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: `Due to a technical issue, the number of Downloads for Nova were incorrectly reported
                                    within the initial release of this report. This data has since been updated and
                                    verified.`,
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>

                        <div style={css['div']}>
                            <div style={css['date']}>
                                {intl.formatMessage({
                                    defaultMessage:
                                        'November 04, 2020: Reissuance of the September 2020 Australian Podcast Ranker',
                                    description: 'report reissuance history',
                                })}
                            </div>

                            <div style={css['history']}>
                                {intl.formatMessage({
                                    defaultMessage: `Due to a technical issue, the Number of New Episodes were incorrectly reported for some
                                    entities within the report. This data has been updated and verified within the report.`,
                                    description: 'report reissuance history',
                                })}
                            </div>
                        </div>
                    </Grid>
                </Stack>
            </Container>
            <TritonFooter />
        </>
    );
};

export default ReportReissuanceHistory;
